'use strict';
const _ = require('lodash');
const moment = require('moment');
const { nanoid } = require('nanoid');
const validators = require('@104ha/input-validators');
const { LOCALES, OPTIONS } = require('../../constants');

const _customizer = (objValue, srcValue, key, object, source, stack) => {
	if (objValue === undefined || srcValue === undefined) return;
	if (_.isPlainObject(objValue) && _.isPlainObject(srcValue)) return _.assignInWith({}, objValue, srcValue, _customizer);
	return objValue;
};

const utils = module.exports = {
	/**
	 * `lodash.defaultsDeep` 會把 array 也都合併起來 (根據 index 檢查 `undefined`)
	 *
	 * 所以只好自訂 customizer (又因為只打算用在 plainObject，所以使用 `assignInWith`)，
	 * 目的是做出 mootools 的 setOptions() 的效果
	 *
	 * **Note:** This method mutates `object`.
	 * @param object
	 * @param sources
	 * @returns {*}
	 */
	defaultsDeepButNotArray: (object, ...sources) => {
		return _.assignInWith(object, ...sources, _customizer);
	},
	validate: validators,
	toObj: src => {
		try {
			return _.isString(src) ? JSON.parse(src) : src;
		} catch (err) {
			return {};
		}
	},
	/**
	 * JSON 欄位解析成物件
	 * @param {*} list 要處理的所有資料
	 * @param {string | string[]} colName 需轉換的所有欄位
	 * @param {function} conv 額外轉換動作
	 * @returns {*} list 的複製體
	 */
	toObj2(list, colName, conv = 0) {
		if (!Array.isArray(colName)) colName = [colName];
		if (!conv) conv = d => d;
		const cnv = ({ ...item }) => {
			colName.forEach(col => item[col] = conv(utils.toObj(item[col]), col));
			return item;
		};

		return Array.isArray(list) ? list.map(cnv) : cnv(list);
	},
	i18n: (src, user, type = 1) => {
		if (!src) {
			return src;
		}

		let localeKey = 'zh_TW';
		if (user && user.locale) localeKey = user.locale;
		let name = src[localeKey];
		//應用
		//1.screen.login 語系 值
		//2.公司語系 值
		//3.依照語言順序 , 取第1個有的值
		//管理 screen.login 語系 值
		if (type === 1) {	//應用
			let coLocale = user.coLocale();
			if (name === '' && src.hasOwnProperty(coLocale)) name = src[coLocale];		//公司語系
			if (name === '') {
				//排除公司語系
				let otherLocale = _.omit(LOCALES, [localeKey, coLocale]);
				//取其他不為空值的語系
				for (let property in otherLocale) {
					if (src.hasOwnProperty(property) && src[property] !== '') {
						name = src[property];
						break;
					}
				}
			}
		}
		return name;
	},
	/**
	 * OptionUtils.getLabel 後端版
	 * @param {string} code sys_code 參數
	 * @param {string | number} val 欄位值
	 * @param {*} user 登入資訊
	 * @returns {string} 定義文字
	 */
	adaptLabel(code, val, user) {
		const list = (OPTIONS[code] || {})[val];
		if (!list) return '';

		const mapLang = Object.keys(LOCALES).reduce((previousValue, currentValue, currentIndex) =>
			({ ...previousValue, [currentValue]: currentIndex }), {});
		let idx = mapLang[user.locale];
		if (list[idx]) return list[idx] || '';

		idx = mapLang[user.coLocale()];
		if (list[idx]) return list[idx] || '';

		return list.reduce((previousValue, currentValue) => previousValue || currentValue) || '';
	},
	/**
	 * option-util 需要的語系索引
	 * @param {*} user 登入資訊
	 */
	i18nIndex: ({ locale }) => Math.max(Object.keys(LOCALES).findIndex(v => v === locale), 0),
	getUTCFormat: (value = null, format = 'YYYY-MM-DD HH:mm:ss') => {
		let utcObj = utils.getUTC(value);
		return utcObj.format(format);
	},
	getUTC: (value = null) => {
		let momentUTC = value ? moment.utc(value) : moment.utc();
		let ts = momentUTC.format('X') * 1;
		return moment.unix(ts).utc();
	},
	uuid: () => { //唯一 key
		return nanoid();
	},
	/**
	 * 判斷重複元素用的工具
	 * @param {*} value 元素值
	 * @param {number} index 元素所在位置
	 * @param {array} self 原始陣列
	 */
	distinct(value, index, self) {
		switch (typeof (value)) {
			case 'number':
			case 'string':
				return value || value === 0 ? self.indexOf(value) === index : false;
			case 'object':
				return value ? self.findIndex(val => JSON.stringify(val) === JSON.stringify(value)) === index : false;
			default:
				return false;
		}
	},
	/**
	 * 產生緊密的、不重複的陣列
	 * @param {array} list 原始陣列
	 */
	fixList(list) {
		if (!list) return [];
		if (!(list instanceof Array)) list = ('' + list).split(',');
		return list.filter(utils.distinct);
	},
	/**
	 * 產生欄位比對函式
	 * @param {string | string[]} colName 比對欄位
	 * @returns {0 | 1 | -1} 比對結果
	 */
	cmpFunc(colName) {
		if (!Array.isArray(colName)) colName = [colName];
		return (a, b) => {
			for (const c of colName) {
				if (a[c] > b[c]) return 1;
				if (a[c] < b[c]) return -1;
			}
			return 0;
		};
	},
	/**
	 * 比對陣列內容是否有異動
	 * <p><b>注意！因為物件內 key 的順序不一定完全相同，在 stringify 後還是有可能被判定為不相等</b></p>
	 * @param {array} list1 原始資料
	 * @param {array} list2 異動資料
	 * @param {function} fnCmp 元素為物件時的排列方法
	 * @returns {number} 傳回 undefined 表示無異動, 否則傳回應寫入的陣列
	 */
	easyCompare(list1, list2, fnCmp) {
		const list3 = utils.fixList(list1).sort(fnCmp);
		const list4 = utils.fixList(list2).sort(fnCmp);
		return JSON.stringify(list3) === JSON.stringify(list4) ? void 0 : list4;
	},
	/**
	 * 把BinaryCheckbox裡面的十進位值，轉成選取item的[index1, index2, ....]
	 * @param {int} dec 十進位的值
	 */
	binaryDecimalToIndex(dec) {
		let binStr = parseInt(dec, 10).toString(2);
		let binArr = binStr.split('');
		let leftArr = [];
		_.forOwnRight(binArr, (value, index) => {
			if (value == '1') leftArr.push(binArr.length - index - 1);
		});
		return leftArr.join(',');
	},
	/**
	 * 把選取item的 index str 字串(逗號串接)，轉成十進位提供給binaryCheckbox使用。
	 * @param {*} indexStr
	 */
	binaryIndexToDecimal(indexStr) {
		let hasComma = _.indexOf(indexStr, ',') >= 0;
		let binArr = hasComma ? indexStr.split(',') : indexStr;
		let sum = 0;
		_.forEach(binArr, (value, index) => {
			let sub = Math.pow(2, value);
			sum += sub;
		});
		return sum;
	},

	/**
	 * 把二次方的OPTIOIN KEY進行加總，最後透過 定義的二次方陣列從大到小取出符合的OPTION值。
	 *
	 * @param {*} dec 原先十進位累積的值
	 * @param {*} count=20 OPTION的數量，預設20
	 */
	decimalToBinaryIndex(dec, count = 20) {

		let binaryArr = [];
		for (let i = count; i--; i > 0) {
			binaryArr.push(Math.pow(2, i));
		}
		// return binaryArr;

		let result = [];
		_.forEach(binaryArr, item => {
			if (dec >= item) {
				dec = dec - item;
				result.push(item);
			}
		});
		return result.reverse();
	},
	toSortedStr: (src) => {
		if (!_.isArray(src)) return '';
		return src.sort((a, b) => a < b ? -1 : a > b ? 1 : 0).join(',');
	},

	// {a1: 1, a2: 0, a3:1} ===> {a: 5}
	convertToBinary: (srcObj, props=[], binaryProp) => {
		srcObj[binaryProp] = _.reduce(props, (result, key, i) => {
			result += +srcObj[key] * Math.pow(2, i);
			delete srcObj[key];
			return result;
		}, 0);
		return srcObj;
	},

	// {a: 5} ===> {a1: 1, a2: 0, a3:1}
	binaryRevert: (srcObj, binaryProp, props=[]) => {
		_.forEach(props, (prop, i) => srcObj[prop] = (srcObj[binaryProp] >>> i) % 2 > 0 ? 1 : 0);
		return srcObj;
	},

	appendUserInfo: (data, user) => {
		data['cUserId'] = user.id;
		data['eUserId'] = user.id;
		return data;
	},
	fixDate: (data) => {
		if (_.isArray(data)) _.forEach(data, d => Utils.fixDate(d));
		else if (_.isObject(data)) _.forEach(data, (value, key) => {
			if (value === '0000-00-00') data[key] = undefined;
		});
		return data;
	},

	/**
	 * 置換 Windows 系統下 不能當作檔名的字元
	 * @param {string} src 原始字串
	 * @param {string} replacement 置換字元
	 * @returns {string} 轉換後字串
	 */
	safeFileName: (src, replacement = '_') => {
		// list of illegal char in file name: https://en.wikipedia.org/wiki/Filename#Reserved_characters_and_words
		if (!_.isString(src) || !_.isString(replacement)) return src;

		return src.replace(/[\/\\?*:|"<>]/g, replacement);
	},
	/**
	 * 時間單位轉換相關
	 * @param fromValue
	 * @param fromUnit
	 * @param toUnit
	 * @param dayToHour
	 * @returns {*}
	 * @private
	 */
	_tranferUnit: (fromValue, fromUnit, toUnit, dayToHour) => {
		const _transToMinutes = (value, unit, dayToHour) => {
			if (unit === 'day')
				return dayToHour * value * 60;
			else if (unit === 'hour')
				return value * 60;
			else if (unit === 'minute')
				return value;
		};

		const _transToHours = (value, unit, dayToHour) => {
			if (unit === 'day')
				return value * dayToHour;
			else if (unit === 'hour')
				return value;
			else if (unit === 'minute')
				return value / 60;
		};

		const _transToDays = (value, unit, dayToHour) => {
			if (unit === 'minute')
				return value / 60 / dayToHour;
			else if (unit === 'hour')
				return value / dayToHour;
			else if (unit === 'day')
				return value;
		};

		let method;

		switch (toUnit) {
			case 'day':
				method = _transToDays;
				break;
			case 'hour':
				method = _transToHours;
				break;
			case 'minute':
				method = _transToMinutes;
				break;
		}
		return method(fromValue, fromUnit, dayToHour);
	},
	_transToMinutes: (value, unit, dayToHour) => {
		if (unit === 'day')
			return dayToHour * value * 60;
		else if (unit === 'hour')
			return value * 60;
		else if (unit === 'minute')
			return value;
	},

	_transToHours: (value, unit, dayToHour) => {
		if (unit === 'day')
			return value * dayToHour;
		else if (unit === 'hour')
			return value;
		else if (unit === 'minute')
			return value / 60;
	},

	_transToDays: (value, unit, dayToHour) => {
		if (unit === 'minute')
			return value / 60 / dayToHour;
		else if (unit === 'hour')
			return value / dayToHour;
		else if (unit === 'day')
			return value;
	},
	/**
	 * 時間單位轉換相關
	 * @param data 原始資料
	 * @param keys 判斷是否合併成同一筆的key array
	 * @returns {array} 會將原資料吐回，但以處理傳入資料
	 * @private
	 */
	rowSpanHandle:(data,keys)=>{
		let group = _.groupBy(data,d=>keys.map(k=>d[k]).join('_'))
		data.forEach((d,idx)=>{
			d.rowSpan = group[keys.map(k=>d[k]).join('_')].length;
			d.newSort = keys.map(k=>d[k]).join('_');
		});
		return data;
	},

	/**
	 * 取得圖片尺寸 (支援 PNG, JPEG, GIF, BMP)
	 * @param {Buffer} buffer 圖片 Buffer
	 * @returns {{width: number, height: number} | null}
	 */
	getImageSize(buffer) {
		try {
			if (!Buffer.isBuffer(buffer)) return null;
			// PNG
			if (buffer[0] === 0x89 && buffer[1] === 0x50 && buffer[2] === 0x4E && buffer[3] === 0x47) {
				const width = buffer.readUInt32BE(16);
				const height = buffer.readUInt32BE(20);
				return { width, height };
			}
			// JPEG
			if (buffer[0] === 0xFF && buffer[1] === 0xD8) {
				let offset = 2;
				while (offset < buffer.length) {
					const marker = buffer.readUInt16BE(offset);
					const length = buffer.readUInt16BE(offset + 2);
					if (marker >= 0xFFC0 && marker <= 0xFFC3) { // SOF0 - SOF3 markers
						const height = buffer.readUInt16BE(offset + 5);
						const width = buffer.readUInt16BE(offset + 7);
						return { width, height };
					}
					offset += 2 + length;
				}
			}
			// GIF
			if (buffer[0] === 0x47 && buffer[1] === 0x49 && buffer[2] === 0x46) {
				const width = buffer.readUInt16LE(6);
				const height = buffer.readUInt16LE(8);
				return { width, height };
			}
			// BMP
			if (buffer[0] === 0x42 && buffer[1] === 0x4D) {
				const width = buffer.readUInt32LE(18);
				const height = buffer.readUInt32LE(22);
				return { width, height };
			}
		} catch (e) {
			// silent error
		}
		return null;
	},

	/**
	 * 計算縮放後的尺寸 (維持比例)
	 * @param {number} srcWidth 原始寬度
	 * @param {number} srcHeight 原始高度
	 * @param {number} maxWidth 最大寬度
	 * @param {number} maxHeight 最大高度
	 * @returns {{width: number, height: number}}
	 */
	calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
		const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight, 1);
		return { width: Math.round(srcWidth * ratio), height: Math.round(srcHeight * ratio) };
	}
};
